import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ContractsPillComponent } from 'src/app/components/contracts-pill/contracts-pill.component';

const GAP = 4;

/**
 * Contract table "Projects" cell (#4865): as many project pills as fit on the one line the row
 * allows, the rest as a "+N" count. The row must stay one line high - the table sizes its pages
 * from a fixed row height - so the cell measures instead of wrapping, and re-measures whenever
 * the column gets wider or narrower (window, detail panel, column resize). Clicking "+N" lets
 * this one row grow to show them all (#4934).
 */
@Component({
  selector: 'app-projects-cell',
  standalone: true,
  imports: [CommonModule, ContractsPillComponent],
  templateUrl: './projects-cell.component.html',
  styleUrls: ['./projects-cell.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectsCellComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() links: any[] | null | undefined = [];

  @ViewChildren('pill', { read: ElementRef }) pills: QueryList<ElementRef<HTMLElement>>;

  /** Until measured, everything renders (none shrinking) so the natural pill widths can be read. */
  visibleCount = Number.MAX_SAFE_INTEGER;

  private pillWidths: number[] | null = null;
  private observer: ResizeObserver | null = null;
  private frame = 0;

  /** All projects shown, wrapping over several lines (#4934). */
  @HostBinding('class.expanded') expanded = false;

  constructor(
    private host: ElementRef<HTMLElement>,
    private cdr: ChangeDetectorRef,
    private zone: NgZone,
  ) {}

  get hiddenCount(): number {
    return Math.max(0, (this.links?.length ?? 0) - this.visibleCount);
  }

  toggle(event: Event): void {
    // Don't let the click reach the grid row (row click opens the contract).
    event.stopPropagation();
    this.expanded = !this.expanded;
    if (!this.expanded) {
      this.scheduleFit();
    }
  }

  ngOnChanges(): void {
    this.expanded = false;
    this.pillWidths = null;
    this.host.nativeElement.classList.add('measuring');
    this.visibleCount = Number.MAX_SAFE_INTEGER;
    this.scheduleFit();
  }

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      this.observer = new ResizeObserver(() => this.scheduleFit());
      this.observer.observe(this.host.nativeElement);
    });
    this.scheduleFit();
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    cancelAnimationFrame(this.frame);
  }

  private scheduleFit(): void {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.fit());
  }

  private fit(): void {
    const count = this.links?.length ?? 0;
    const available = this.host.nativeElement.clientWidth;
    if (this.expanded || !count || !available || !this.pills) {
      return;
    }

    if (!this.pillWidths) {
      // Natural widths, read while every pill is rendered (they don't shrink, see the CSS).
      this.pillWidths = this.pills.map(p => p.nativeElement.offsetWidth);
      this.host.nativeElement.classList.remove('measuring');
    }

    const next = visibleThatFit(this.pillWidths, available);
    if (next !== this.visibleCount) {
      this.zone.run(() => {
        this.visibleCount = next;
        this.cdr.detectChanges();
      });
    }
  }
}

/**
 * How many pills fit in `available` px, keeping room for the "+N" count when not all of them do.
 * Never less than one: the first pill then shrinks with "..." instead.
 */
export function visibleThatFit(widths: number[], available: number): number {
  const total = widths.reduce((sum, w) => sum + w, 0) + GAP * (widths.length - 1);
  if (total <= available) {
    return widths.length;
  }

  let used = 0;
  let fitting = 0;
  for (let i = 0; i < widths.length; i++) {
    const hidden = widths.length - (i + 1);
    const counter = hidden > 0 ? GAP + counterWidth(hidden) : 0;
    const next = used + (i > 0 ? GAP : 0) + widths[i];
    if (next + counter > available) {
      break;
    }
    used = next;
    fitting = i + 1;
  }
  return Math.max(1, fitting);
}

/** "+N" as plain text: ~8px per character at 13px, plus a little slack. */
function counterWidth(hidden: number): number {
  return 4 + 8 * (`${hidden}`.length + 1);
}
